import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpOptionsInterface, HttpOptionsService } from './http-options.service';
import { HttpClient } from '@angular/common/http';
import { Observable, switchMap } from 'rxjs';
import { isPlatformServer } from '@angular/common';
import { AppConfigInterface } from '../../config/app.config.interface';
import { APP_CONFIG } from '../../app.config';

@Injectable({
  providedIn: 'root',
})
export class ApiClientService {
  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    @Inject(APP_CONFIG) private config: AppConfigInterface,
    private httpClient: HttpClient,
    private httpOptionsService: HttpOptionsService,
  ) {
  }

  public get<T>(
    endPoint: string,
    useToken: boolean,
    params?: NestedDictionary<any>, // eslint-disable-line @typescript-eslint/no-explicit-any
    withCredentials = false,
    responseType = 'json',
  ): Observable<T> {
    // if (isPlatformServer(this.platformId)) {
    //   throw Error('Not supported use api client on server - GET: ' + endPoint);
    // }

    return this.httpOptionsService.getOptions(useToken, withCredentials, params, responseType)
      .pipe(
        switchMap((options: HttpOptionsInterface) => {
          return this.httpClient.get<T>(this.config.apiURL + endPoint, options);
        }),
      );
  }

  public delete<T>(
    endPoint: string,
    useToken: boolean,
    params?: NestedDictionary<any>, // eslint-disable-line @typescript-eslint/no-explicit-any
    withCredentials = false,
  ): Observable<T> {
    if (isPlatformServer(this.platformId)) {
      throw Error('Not supported use api client on server - DELETE: ' + endPoint);
    }

    return this.httpOptionsService.getOptions(useToken, withCredentials, params)
      .pipe(
        switchMap((options: HttpOptionsInterface) => {
          return this.httpClient.delete<T>(this.config.apiURL + endPoint, options);
        }),
      );
  }

  public post<T>(
    endPoint: string,
    useToken: boolean,
    params: any, // eslint-disable-line @typescript-eslint/no-explicit-any
    ownHttpOptionsSupplier?: HttpOptionsService,
    withCredentials = false,
    responseType = 'json',
  ): Observable<T> {
    // if (isPlatformServer(this.platformId)) {
    //   throw Error('Not supported use api client on server - POST: ' + endPoint);
    // }

    const httpOptionsService: HttpOptionsService = ownHttpOptionsSupplier ? ownHttpOptionsSupplier : this.httpOptionsService;

    let headers = undefined;
    if (params?.['headers']) {
      headers = {
        headers: params?.['headers'],
      };
    }

    return httpOptionsService.getOptions(useToken, withCredentials, headers, responseType)
      .pipe(
        switchMap((options: HttpOptionsInterface) => {
          delete params?.['headers'];
          return this.httpClient.post<T>(
            this.config.apiURL + endPoint,
            params,
            options,
          );
        }),
      );
  }

  public put<T>(
    endPoint: string,
    useToken: boolean,
    params: any, // eslint-disable-line @typescript-eslint/no-explicit-any
    withCredentials = false,
  ): Observable<T> {
    if (isPlatformServer(this.platformId)) {
      throw Error('Not supported use api client on server - PUT: ' + endPoint);
    }

    return this.httpOptionsService.getOptions(useToken, withCredentials)
      .pipe(
        switchMap((options: HttpOptionsInterface) => {
          return this.httpClient.put<T>(
            this.config.apiURL + endPoint,
            this.getBody(params),
            options,
          );
        }),
      );
  }

  public patch<T>(
    endPoint: string,
    useToken: boolean,
    params: NestedDictionary<any>, // eslint-disable-line @typescript-eslint/no-explicit-any
    withCredentials = false,
  ): Observable<T> {
    if (isPlatformServer(this.platformId)) {
      throw Error('Not supported use api client on server - PATCH: ' + endPoint);
    }

    return this.httpOptionsService.getOptions(useToken, withCredentials)
      .pipe(
        switchMap((options: HttpOptionsInterface) => {
          return this.httpClient.patch<T>(
            this.config.apiURL + endPoint,
            this.getBody(params),
            options,
          );
        }),
      );
  }

  private getBody(body: NestedDictionary<string>): NestedDictionary<string> | '' {
    return body ?? '';
  }
}
